import { all, put, takeLatest } from "redux-saga/effects";
import {
  componentKey,
  setCloseAddTaskDialog,
  setTaskTaskListData,
  setCloseCreateGroupDialog,
  setGroupListData,
  setAssignToListData,
  setTaskTypeListData,
} from "./taskSlice";
import store from "../../store/store";
import {
  hideLoader,
  showLoader,
} from "../../component-lib/Molecules/loader/LoaderSlice";
import { eventEHRCustomToastMessage } from "../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";
import TaskService from "../../services/TaskService";
import SettingsService from "../../services/SettingsService";

export const { postTaskData, getTaskList, updateTaskData, deleteTasks, createGroup, updateGroup, getGroupList, deleteGroup, getAssignToList, getTaskTypeList } = {
  postTaskData: (payload) => {
    return {
      type: "PATIENT/POST_TASK_DATA",
      payload,
    };
  },
  getTaskList: (payload) => {
    return {
      type: "PATIENT/GET_TASK_LIST",
      payload,
    };
  },
  updateTaskData: (payload) => {
    return {
      type: "PATIENT/UPDATE_TASK_DATA",
      payload,
    };
  },
  deleteTasks: (payload) => {
    return {
      type: "PATIENT/DELETE_TASKS",
      payload,
    };
  },
  createGroup: (payload) => {
    return {
      type: "PATIENT/CREATE_GROUP",
      payload,
    };
  },
  updateGroup: (payload) => {
    return {
      type: "PATIENT/UPDATE_GROUP",
      payload,
    };
  },
  getGroupList: (payload) => {
    return {
      type: "PATIENT/GET_GROUP_LIST",
      payload,
    };
  },
  deleteGroup: (payload) => {
    return {
      type: "PATIENT/DELETE_GROUP",
      payload,
    };
  },
  getAssignToList: (payload) => {
    return {
      type: "PATIENT/GET_ASSIGN_TO_LIST",
      payload,
    };
  },
  getTaskTypeList: (payload) => {
    return {
      type: "PATIENT/GET_TASK_TYPE_LIST",
      payload,
    };
  },
};

// POST TASK DATA ASYNC
function* postTaskDataAsync(action) {
  // const { apiPayload } = action?.payload;
  try {
    yield put(showLoader());
    const response = yield TaskService.postTaskData(action?.payload);
    const { status } = response;
    if (status) {
      const { data, message } = response?.data;

      eventEHRCustomToastMessage({
        title: `${message}`,
        status: "success",
      });
      yield put(setCloseAddTaskDialog(true));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setCloseAddTaskDialog(false));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// GET TASK DATA ASYNC
function* getTaskListAsync(action) {
  try {
    yield put(showLoader());
    const response = yield TaskService.getTaskList(action?.payload);
    const { status } = response;
    if (status) {
      const { data, message } = response?.data;
      yield put(setTaskTaskListData(data));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setTaskTaskListData({}));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// UPDATE TASK DATA ASYNC
function* updateTaskDataAsync(action) {
  const { apiPayload, taskId } = action?.payload;
  try {
    yield put(showLoader());
    const response = yield TaskService.updateTaskData(taskId, apiPayload);
    const { status } = response;
    if (status) {
      const { data, message } = response?.data;

      eventEHRCustomToastMessage({
        title: `${message}`,
        status: "success",
      });
      yield put(setCloseAddTaskDialog(true));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setCloseAddTaskDialog(false));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// DELETE TASKS ASYNC (single or bulk — a single delete sends one id)
function* deleteTasksAsync(action) {
  const { taskIds, refreshPayload } = action?.payload || {};
  try {
    yield put(showLoader());
    const response = yield TaskService.deleteTasks(taskIds);
    const { status } = response;
    if (status) {
      const { message } = response?.data;
      eventEHRCustomToastMessage({
        title: message || "Task(s) deleted successfully",
        status: "success",
      });
      // Refetch with the caller's current page / rows-per-page / filters so the
      // list doesn't silently jump back to an unfiltered page 1.
      yield put(getTaskList(refreshPayload));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// CREATE GROUP ASYNC
function* createGroupAsync(action) {
  try {
    yield put(showLoader());
    const response = yield TaskService.createGroup(action?.payload);
    const { status } = response;
    if (status) {
      const { data, message } = response?.data;

      eventEHRCustomToastMessage({
        title: `${message}`,
        status: "success",
      });
      yield put(setCloseCreateGroupDialog(true));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setCloseCreateGroupDialog(false));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// UPDATE GROUP ASYNC
function* updateGroupAsync(action) {
  const { apiPayload, groupId } = action?.payload;
  try {
    yield put(showLoader());
    const response = yield TaskService.updateGroup(groupId, apiPayload);
    const { status } = response;
    if (status) {
      const { data, message } = response?.data;

      eventEHRCustomToastMessage({
        title: `${message}`,
        status: "success",
      });
      yield put(setCloseCreateGroupDialog(true));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setCloseCreateGroupDialog(false));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// GET GROUP LIST ASYNC
function* getGroupListAsync(action) {
  try {
    yield put(showLoader());
    const response = yield TaskService.getGroupList(action?.payload);
    const { status } = response;
    if (status) {
      const { data, message } = response;
      yield put(setGroupListData(data));
    } else {
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setGroupListData({}));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// DELETE GROUP ASYNC
function* deleteGroupAsync(action) {
  try {
    yield put(showLoader());
    const response = yield TaskService.deleteGroup(action?.payload);
    const { status } = response;
    if (status) {
      const { message } = response?.data;
      eventEHRCustomToastMessage({
        title: `${message}`,
        status: "success",
      });
      // Refresh the group list after successful deletion
      yield put(getGroupList({ pageNumber: 1, limit: 10 }));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// GET ASSIGN TO LIST ASYNC
function* getAssignToListAsync(action) {
  try {
    yield put(showLoader());
    const response = yield TaskService.getAssignToList(action?.payload);
    const { status } = response;
    if (status) {
      const { data, message } = response;
      // Keep the request params with the response so the consumer can tell
      // which request it belongs to and discard superseded ones (e.g. the
      // unfiltered page-1 fetch landing after the user already typed a search).
      yield put(
        setAssignToListData({ ...data, requestMeta: action?.payload })
      );
    } else {
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setAssignToListData({}));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// GET TASK TYPE LIST ASYNC (master data, type = task_type)
function* getTaskTypeListAsync() {
  try {
    const first = yield SettingsService.getMasterDataList({
      type: "task_type",
      pageNumber: 1,
      limit: 200,
    });
    if (first?.status) {
      let rows = first?.data?.masterData ?? [];
      const totalPages = first?.data?.totalPages ?? 1;
      if (totalPages > 1) {
        const rest = yield Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            SettingsService.getMasterDataList({
              type: "task_type",
              pageNumber: i + 2,
              limit: 200,
            }).then((r) => r?.data?.masterData ?? [])
          )
        );
        rows = rows.concat(...rest);
      }
      yield put(setTaskTypeListData(rows));
    }
  } catch (error) {
    yield put(setTaskTypeListData([]));
    console.log("err: ", error);
  }
}

function* rootSaga() {
  yield all([takeLatest(postTaskData().type, postTaskDataAsync)]);
  yield all([takeLatest(getTaskList().type, getTaskListAsync)]);
  yield all([takeLatest(updateTaskData().type, updateTaskDataAsync)]);
  yield all([takeLatest(deleteTasks().type, deleteTasksAsync)]);
  yield all([takeLatest(createGroup().type, createGroupAsync)]);
  yield all([takeLatest(updateGroup().type, updateGroupAsync)]);
  yield all([takeLatest(getGroupList().type, getGroupListAsync)]);
  yield all([takeLatest(deleteGroup().type, deleteGroupAsync)]);
  yield all([takeLatest(getAssignToList().type, getAssignToListAsync)]);
  yield all([takeLatest(getTaskTypeList().type, getTaskTypeListAsync)]);
}

store.sagaManager.addSaga(componentKey, rootSaga);
